import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, ResponsiveContainer
} from 'recharts';

const C = {
  bg:       '#0d1117',
  card:     '#161b22',
  border:   '#21262d',
  text:     '#e6edf3',
  muted:    '#8b949e',
  blue:     '#4a9eff',
  green:    '#36d399',
  orange:   '#f6c90e',
  lilla:    '#a855f7',
  roed:     '#f87171',
};

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  if (v >= 1000000) return (v / 1000000).toFixed(2) + 'M kr';
  if (v >= 1000)    return Math.round(v / 1000) + 'k kr';
  return Math.round(v) + ' kr';
};

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: <strong>{fmt(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

export default function MRRScenariePanelSR() {
  const [data, setData]         = useState([]);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [fejl, setFejl]         = useState(null);
  const [visForecast, setVisForecast]   = useState(true);
  const [visScenarier, setVisScenarier] = useState(true);

  useEffect(() => {
    fetch('/api/os/strategisk/mrr-scenarie', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.fejl) { setFejl(d.fejl); return; }
        setData(d.data || []);
        setMeta(d.meta || {});
      })
      .catch(e => setFejl(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: 32, color: C.muted, textAlign: 'center' }}>Henter MRR-data...</div>
  );
  if (fejl) return (
    <div style={{ padding: 32, color: C.roed }}>Fejl: {fejl}</div>
  );

  const maxVal = Math.max(
    ...data.map(d => Math.max(d.mrr || 0, d.forecast || 0)),
    meta.scenarie_ambitioes || 0
  ) * 1.1;

  const btnStyle = (active) => ({
    padding: '4px 12px',
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: active ? C.blue : C.card,
    color: active ? '#fff' : C.muted,
    cursor: 'pointer',
    fontSize: 12,
    minHeight: 36,
  });

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '20px 16px', color: C.text, fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>MRR vs. Scenarie-maal</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            Maanedlig vækst · vækstrate: {meta.slope > 0 ? '+' : ''}{fmt(meta.slope)}/md
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btnStyle(visForecast)} onClick={() => setVisForecast(v => !v)}>
            {visForecast ? '✓' : '○'} Forecast
          </button>
          <button style={btnStyle(visScenarier)} onClick={() => setVisScenarier(v => !v)}>
            {visScenarier ? '✓' : '○'} Scenarier
          </button>
        </div>
      </div>

      {/* Chart */}
      <div style={{ width: '100%', height: 360 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="maaned" stroke={C.muted} tick={{ fontSize: 11 }} />
            <YAxis
              tickFormatter={v => v >= 1000 ? Math.round(v/1000) + 'k' : v}
              stroke={C.muted}
              domain={[0, maxVal]}
              tick={{ fontSize: 11 }}
              width={50}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: C.muted }} />

            {/* Faktisk MRR */}
            <Bar dataKey="mrr" name="Faktisk MRR" fill={C.blue} radius={[4,4,0,0]} maxBarSize={40} />

            {/* Forecast */}
            {visForecast && (
              <Line
                dataKey="forecast"
                name="Forecast"
                stroke={C.green}
                strokeDasharray="6 3"
                strokeWidth={2}
                dot={{ r: 3, fill: C.green }}
                connectNulls={false}
              />
            )}

            {/* Scenarie-linjer */}
            {visScenarier && (
              <>
                <ReferenceLine y={meta.loen_forpligtelse}   label={{ value: 'Loenforpligtelse', fill: C.roed,   fontSize: 11, position: 'insideTopLeft' }} stroke={C.roed}   strokeDasharray="4 2" />
                <ReferenceLine y={meta.scenarie_realistisk} label={{ value: 'Realistisk/md',    fill: C.orange, fontSize: 11, position: 'insideTopLeft' }} stroke={C.orange} strokeDasharray="4 2" />
                <ReferenceLine y={meta.scenarie_ambitioes}  label={{ value: 'Ambitioes/md',     fill: C.lilla,  fontSize: 11, position: 'insideTopLeft' }} stroke={C.lilla}  strokeDasharray="4 2" />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legende */}
      {visScenarier && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
          {[
            { label: 'Loenforpligtelse', vaerdi: meta.loen_forpligtelse,   farve: C.roed   },
            { label: 'Realistisk (3.5M)', vaerdi: meta.scenarie_realistisk, farve: C.orange },
            { label: 'Ambitioes (5M)',    vaerdi: meta.scenarie_ambitioes,  farve: C.lilla  },
          ].map(s => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <div style={{ width: 24, height: 2, background: s.farve }} />
              <span style={{ color: C.muted }}>{s.label}:</span>
              <span style={{ color: s.farve, fontWeight: 600 }}>{fmt(s.vaerdi)}/md</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}